/**
 * "Create line item" for an approved trip claim — ONE prefill path, used
 * wherever a claim is reviewed (the booking's Driver Events tab, the Order
 * Details banner, the Trip claims page). It only seeds the EXISTING
 * adjustments form (Order Details' Order Ledger tab, `lineItem`); nothing is
 * saved until the admin saves it there.
 */
import { expenseAdjustmentDescription } from '../../../Utils/tripClaims';

export const WAITING_CHARGE_NOTE = 'Amount left blank on purpose: price the waiting time from the '
  + 'vehicle category / operator waiting rate, then enter it as a customer charge and/or operator amount.';

export function waitingChargeDescription(totalMinutes) {
  return totalMinutes > 0
    ? `Extra waiting time — ${totalMinutes} min`
    : 'Extra waiting time';
}

// The operator an expense's adjustment should be attributed to. Uses the
// expense's own operator when it is on this booking, else the booking's only
// operator; with several operators and no match the admin picks one.
export function resolveExpenseOperatorId(order, expense) {
  const opIds = (order?.operators?.length
    ? order.operators.map((op) => op.operator_id)
    : [order?.operator_id]
  ).filter(Boolean);
  if (expense?.operator_id && opIds.includes(expense.operator_id)) return expense.operator_id;
  return opIds.length === 1 ? opIds[0] : '';
}

/** An approved receipt: description, amount owed to the operator, currency. */
export function buildExpensePrefill(order, expense) {
  const targetOperatorId = resolveExpenseOperatorId(order, expense);
  return {
    prefill: {
      description: expenseAdjustmentDescription(expense),
      currency: expense.currency === 'HUF' ? 'HUF' : 'EUR',
      operator_adjustment: Number(expense.amount) || 0,
      target_operator_id: targetOperatorId,
    },
    prefillNote: 'Pre-filled from the driver\'s approved receipt as an amount owed to the operator '
      + `(they paid it). If the customer is re-billed, also enter it as a customer amount.${
        targetOperatorId ? '' : ' Pick the operator it belongs to first.'}`,
  };
}

/**
 * An approved overtime entry: the description only ("Extra waiting time — N
 * min") and NO amount — there is no waiting rate to price it with here.
 */
export function buildOvertimePrefill(order, entry) {
  return {
    prefill: { description: waitingChargeDescription(Number(entry?.minutes) || 0) },
    prefillNote: WAITING_CHARGE_NOTE,
  };
}

/**
 * The prefill for "Create line item" on an approved claim, whichever kind:
 * `{ kind: 'receipt', expense }` or `{ kind: 'overtime', entry }`.
 */
export const lineItemPrefillFor = (order, claim) => (claim.kind === 'receipt'
  ? buildExpensePrefill(order, claim.expense)
  : buildOvertimePrefill(order, claim.entry));
